import { langueCourante, traduire } from '../../i18n/language'

/**
 * Retrait de l'historique (décision D2 du propriétaire, 02/11, R2) : les routes /api/v1 étendues par la vague V2 ne servent plus
 * que leur corps v10 (profils) ; un corps historique (rôle) reçoit CORPS_HISTORIQUE_RETIRE (400). Les écrans historiques qui
 * choisissent encore un rôle (annuaire, fiche, invitations, membres du projet et du programme) passent par cette traduction,
 * adaptation minimale jusqu'à leur remplacement par la coquille v10 (R3). Elle suit la projection des graines (R0 (d)) :
 * propriétaire → administrateur du niveau, contributeur → développeur, lecteur → lecteur ; à l'organisation, seule
 * l'administration (org_admin) et l'audit (auditor) posent un profil.
 */
export type RoleEspaceHistorique = 'owner' | 'contributor' | 'viewer'
export type NiveauEspace = 'projet' | 'programme'

/** Profils posés sur un projet ou un programme pour un rôle choisi dans un écran historique. */
export function profilsDuRole(niveau: NiveauEspace, role: string): string[] {
  if (niveau === 'programme') {
    // Le catalogue n'a pas de profil de programme « contributeur » : lecteur du programme, jamais davantage.
    return role === 'owner' ? ['administrateur-programme'] : ['lecteur-programme']
  }
  if (role === 'owner') return ['administrateur-projet']
  if (role === 'contributor') return ['developpeur']
  return ['lecteur']
}

/** Profils posés à l'organisation pour un rôle d'organisation choisi dans un écran historique ; vide = membre simple. */
export function profilsOrganisationDuRole(role: string): string[] {
  // « owner » d'organisation n'était pas une administration (rôles d'espace seulement) : membre simple.
  if (role === 'org_admin') return ['administrateur-organisation']
  if (role === 'auditor') return ['auditeur']
  return []
}

/** Niveau v10 d'une portée historique. */
export const niveauDePortee = (scope: string): NiveauEspace => (scope === 'program' ? 'programme' : 'projet')

/** En-tête If-Match d'une version lue (guillemets ajoutés si absents). */
export const siVersionLue = (version: string | null | undefined): Record<string, string> =>
  version ? { 'If-Match': version.startsWith('"') ? version : `"${version}"` } : {}

/**
 * Critique codex t1, constat 9 : l'ajout demandé avec un rôle d'écriture se fait en deux actes (arrivée en lecteur, puis
 * profils). Si le second échoue, le premier a déjà eu lieu : l'erreur le dit (la personne est membre, en lecteur) au lieu
 * d'annoncer un échec complet. `membre` est la réponse du premier acte, `cause` l'erreur du second.
 */
export class AjoutPartielError<M = unknown> extends Error {
  readonly code = 'AJOUT_PARTIEL'
  constructor(readonly membre: M, readonly cause: unknown) {
    const detail = cause instanceof Error && cause.message ? ` : ${cause.message}` : ''
    super(`${traduire(langueCourante(), 'org.membreAjoutPartiel')}${detail}`)
    this.name = 'AjoutPartielError'
  }
}

/** Deux actes explicites : le second qui échoue rend une {@link AjoutPartielError} portant le résultat du premier. */
export async function ajouterEnDeuxActes<M, R>(arrivee: () => Promise<M>, profils: ((membre: M) => Promise<R>) | null): Promise<M | R> {
  const membre = await arrivee()
  if (!profils) return membre
  try {
    return await profils(membre)
  } catch (cause) {
    throw new AjoutPartielError(membre, cause)
  }
}
